import { ComponentProps, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Button, F0Checkbox } from '@factorialco/f0-react'
import { F0Select } from '@factorialco/f0-react'
import {
  EntitySelectEntity,
  Input,
  OneModal,
  Spinner,
} from '@factorialco/f0-react/dist/experimental'
import {
  AccessCalendarSyncItemFragment,
  CalendarSyncGetEmployeeAccessDocument,
  MeetingsValueObjectsDeletionTypeEnum,
  OneOnOneCreateMeetingDocument,
  OneOnOnesEmployeesDocument,
  OneOnOnesScheduledMeetingsDocument,
  OneOnOnesTeamTableEmployeesDocument,
} from 'generated/resources/graphql'
import moment from 'moment-timezone'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { AvailabilityViewer } from 'modules/cronofy/components/AvailabilityViewer'
import { useGenerateElementToken } from 'modules/cronofy/hooks/useGenerateElementToken'
import EmployeeSelector from 'modules/meetings/components/EmployeeSelector'
import { SelectTimeSlot } from 'modules/meetings/components/SelectTimeSlot'
import { getRoundedHour } from 'modules/meetings/lib/utils/dates'
import { useAccessCalendarSync } from 'modules/personal_settings/components/Settings/ExternalCalendar/useAccessCalendarSync'

import { UpsellAlert } from './components/UpsellAlert'
import './cronofyStyles.css'

interface BuildAvailabilityOptionsProps {
  accessCalendarSyncs: AccessCalendarSyncItemFragment[]
  userTimeZone: string
  locale: string
  duration: string
  firstDayOfWeek?: string | null
}
const buildAvailabilityOptions = ({
  accessCalendarSyncs,
  userTimeZone,
  locale,
  duration,
  firstDayOfWeek,
}: BuildAvailabilityOptionsProps) => {
  const today = new Date()
  const endDate = new Date()
  endDate.setDate(endDate.getDate() + 30)

  return {
    availability_query: {
      participants: [
        {
          required: 'all' as const,
          members: accessCalendarSyncs
            .map((accessCalendarSync) => accessCalendarSync.sub)
            .filter((sub): sub is string => Boolean(sub))
            .map((sub) => ({ sub })),
        },
      ],
      query_periods: [{ start: today.toISOString(), end: endDate.toISOString() }],
      required_duration: { minutes: parseInt(duration) || 45 },
      include_member_statuses: true,
    },
    config: {
      start_time: '09:00',
      end_time: '17:00',
      interval: 15,
      slot_selection: 'unrestricted' as const,
      allow_expansion: true,
      mode: 'no_confirm' as const,
      week_start_day: firstDayOfWeek ?? ('monday' as const),
    },
    tzid: userTimeZone,
    locale: locale,
    styles: {
      colors: {
        hairline: '#05234824',
        primary: '#fff',
        available: '#fff',
        unavailable: '#0523481A',
        availableHover: '#07A0AB1A',
        availableActive: '#068089',
        unavailableHover: '#F974151A',
        unavailableActive: '#F97415',
      },
    },
  }
}

interface CreateOneOnOneModalProps {
  openModal?: boolean
  setOpenModal: (open: boolean) => void
  defaultSelectedEmployee?: EntitySelectEntity
  canSeeAdvancedMeetings: boolean
}

interface CreateMeetingForm {
  employees: EntitySelectEntity[]
  date: string
  startTime: string
  duration: string
  frequency?: MeetingsValueObjectsDeletionTypeEnum
}

export const CreateOneOnOneModal = ({
  openModal = true,
  setOpenModal,
  defaultSelectedEmployee,
  canSeeAdvancedMeetings,
}: CreateOneOnOneModalProps) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const {
    employee: currentEmployee,
    access: { id: accessId, locale, firstDayOfWeek },
  } = useCurrentSession()

  const userTimeZone = moment.tz.guess()
  const [elementToken, setElementToken] = useState<string | undefined>(undefined)
  const [availabilityOptions, setAvailabilityOptions] =
    useState<ComponentProps<typeof AvailabilityViewer>['options']>()
  const { generate, loading: isLoadingGenerateElementToken } = useGenerateElementToken()

  const [createMeetingsMeeting, { loading: isLoadingCreateMeeting }] = useMutation(
    OneOnOneCreateMeetingDocument,
    {
      extractorFn: (data) => data.meetingsMutations.createMeetingsTemplate,
      refetchQueries: [
        OneOnOnesEmployeesDocument,
        OneOnOnesScheduledMeetingsDocument,
        OneOnOnesTeamTableEmployeesDocument,
      ],
    }
  )

  const form = useForm<CreateMeetingForm>({
    defaultValues: {
      employees: defaultSelectedEmployee ? [defaultSelectedEmployee] : [],
      date: moment().format('YYYY-MM-DD'),
      startTime: getRoundedHour(),
      duration: '45',
      frequency: undefined,
    },
    validate(values) {
      const errors: FormErrors<CreateMeetingForm> = {}

      if (values.employees.length === 0) {
        errors.employees = i18n.t('common.validations.empty')
      }

      if (!values.date) {
        errors.date = i18n.t('common.validations.empty')
      }

      if (!values.startTime) {
        errors.startTime = i18n.t('common.validations.empty')
      }

      if (!values.duration) {
        errors.duration = i18n.t('common.validations.empty')
      }

      return errors
    },

    async onSubmit(values, { setErrors }) {
      const selectedEmployee = values.employees[0]

      const variables = {
        date: moment(`${values.date} ${values.startTime}`).utc().format('YYYY-MM-DD'),
        startTime: moment(`${values.date} ${values.startTime}`).utc().toISOString(),
        endTime: moment(`${values.date} ${values.startTime}`)
          .add(values.duration, 'minutes')
          .utc()
          .toISOString(),
        attendeeIds: [Number(currentEmployee?.id), Number(selectedEmployee?.id)],
        timezone: userTimeZone,
        frequency: values.frequency,
      }

      const result = await createMeetingsMeeting({
        variables,
      })

      const handler = handleResult(result, (errors) => setErrors(errors))
      const templateId = handler?.success ? handler.data?.template?.id : undefined

      if (templateId) {
        track(trackingEvents.meetings.CreateMeetingSuccess, {
          ...variables,
        })
        setOpenModal(false)
        navigate(navigation.engagement.one_on_ones.templateId.fullPath({ templateId }))
      } else {
        track(trackingEvents.meetings.CreateMeetingError, {
          ...variables,
        })
        app.setFlash({
          type: 'wrong',
          message: i18n.t('common.error'),
        })
      }

      return handler
    },
  })

  const { data: selectedEmployeeAccess, loading: isLoadingSelectedEmployeeAccess } = useQuery(
    CalendarSyncGetEmployeeAccessDocument,
    {
      variables: {
        employeeId: Number(form.values.employees[0]?.id ?? defaultSelectedEmployee?.id),
      },
      skip: !form.values.employees[0]?.id && !defaultSelectedEmployee?.id,
    }
  )

  const selectedEmployeeAccessId =
    selectedEmployeeAccess?.employees?.employeesConnection?.nodes?.[0]?.access?.id ?? undefined

  const { accessCalendarSync, loading: loadingCalendar } = useAccessCalendarSync(
    canSeeAdvancedMeetings ? accessId : undefined
  )
  const { accessCalendarSync: selectedEmployeeCalendarSync } =
    useAccessCalendarSync(selectedEmployeeAccessId)

  // Generate element token for availability viewer
  const generateElementToken = async () => {
    if (!accessId || loadingCalendar || !accessCalendarSync) return

    const result = await generate({
      id: accessCalendarSync.id,
      attendeesAccessesIds: [accessId, selectedEmployeeAccessId ?? false].filter(Boolean),
    })

    const handled = handleResult(result, () => undefined)
    const token = handled.success && handled.data.calendarSync?.elementToken
    if (!!token) {
      setElementToken(token)
    }
  }

  // Set availability options when duration changes
  useEffect(() => {
    if (!form.values.duration || !accessCalendarSync) return

    const calendarSyncs = [accessCalendarSync, selectedEmployeeCalendarSync].filter(Boolean)
    if (calendarSyncs.length === 0) return

    setAvailabilityOptions(
      buildAvailabilityOptions({
        accessCalendarSyncs: calendarSyncs,
        userTimeZone,
        locale,
        duration: form.values.duration,
        firstDayOfWeek,
      })
    )
  }, [
    form.values.duration,
    accessCalendarSync,
    selectedEmployeeCalendarSync,
    userTimeZone,
    locale,
    firstDayOfWeek,
  ])

  // Generate element token when component mounts
  useEffect(() => {
    generateElementToken()
  }, [accessId, loadingCalendar, accessCalendarSync, selectedEmployeeAccessId])

  const canUseAvailabilityViewer = !!elementToken && !!availabilityOptions && canSeeAdvancedMeetings

  return (
    <OneModal
      position="right"
      isOpen={openModal}
      onClose={() => {
        if (!isLoadingCreateMeeting) {
          setOpenModal(false)
        }
      }}
    >
      {isLoadingCreateMeeting ? (
        <OneModal.Content>
          <Box
            paddingX="s16"
            paddingY="s120"
            gap="s12"
            alignItems="center"
            justifyContent="center"
            height="full"
          >
            <Spinner />
            <Text.Normal size="200">
              {i18n.t('meetings.create_scheduled_meeting.loading')}
            </Text.Normal>
          </Box>
        </OneModal.Content>
      ) : (
        <>
          <OneModal.Header title={i18n.t('meetings.create_scheduled_meeting.title')} />
          {loadingCalendar ? (
            <OneModal.Content>
              <Box
                paddingX="s16"
                paddingBottom="s64"
                paddingTop="s64"
                gap="s12"
                alignItems="center"
                justifyContent="center"
              >
                <Spinner />
              </Box>
            </OneModal.Content>
          ) : (
            <OneModal.Content withPadding>
              <Box gap="s12">
                <Box flexDirection="row" gap="s12">
                  <Box gap="s8" style={{ width: '50%' }}>
                    <Text.Normal size="200" weight="medium">
                      {i18n.t('meetings.create_scheduled_meeting.employee')}
                    </Text.Normal>

                    <EmployeeSelector
                      onlyMyReportees={false}
                      disabled={!!defaultSelectedEmployee}
                      employees={form.values.employees}
                      setEmployees={(employees) => form.setFieldValue('employees', employees)}
                      singleSelector
                      error={
                        form.errors.employees && typeof form.errors.employees === 'string'
                          ? form.errors.employees
                          : undefined
                      }
                    />
                  </Box>
                  <Box gap="s8" style={{ width: '50%' }}>
                    <SelectTimeSlot
                      label={i18n.t('meetings.create_scheduled_meeting.duration')}
                      onChange={(value) => form.setFieldValue('duration', value)}
                      value={form.values.duration}
                      error={form.errors.duration}
                    />
                  </Box>
                </Box>

                <Box gap="s8">
                  <F0Checkbox
                    checked={!!form.values.frequency}
                    onCheckedChange={() => {
                      if (form.values.frequency) form.setFieldValue('frequency', undefined)
                      else form.setFieldValue('frequency', 'weekly')
                    }}
                    title={i18n.t('meetings.create_scheduled_meeting.repeat')}
                  />
                  {form.values.frequency && (
                    <F0Select
                      label={i18n.t('meetings.create_scheduled_meeting.repeats')}
                      placeholder={i18n.t(
                        'meetings.create_scheduled_meeting.frequency_placeholder'
                      )}
                      value={form.values.frequency}
                      onChange={(value: MeetingsValueObjectsDeletionTypeEnum) =>
                        form.setFieldValue('frequency', value)
                      }
                      error={form.errors.frequency}
                      options={[
                        {
                          label: i18n.t('meetings.create_scheduled_meeting.weekly'),
                          value: 'weekly',
                        },
                        {
                          label: i18n.t('meetings.create_scheduled_meeting.monthly'),
                          value: 'monthly',
                        },
                      ]}
                    />
                  )}
                </Box>

                {isLoadingGenerateElementToken || isLoadingSelectedEmployeeAccess ? (
                  <Box
                    height="s360"
                    flexDirection="row"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Spinner />
                  </Box>
                ) : canUseAvailabilityViewer ? (
                  <Box width="full" overflowX="hidden">
                    <AvailabilityViewer
                      elementToken={elementToken}
                      options={availabilityOptions}
                      onChange={(date) => {
                        if (date) {
                          const selectedDate = moment(date)
                          form.setFieldValue('date', selectedDate.format('YYYY-MM-DD'))
                          form.setFieldValue('startTime', selectedDate.format('HH:mm'))
                        }
                      }}
                    />
                  </Box>
                ) : (
                  <Box gap="s12" flexDirection="row">
                    <Box gap="s8" style={{ width: '50%' }}>
                      <Input
                        label={i18n.t('meetings.create_scheduled_meeting.date')}
                        type="date"
                        value={form.values.date}
                        onChange={(value) => form.setFieldValue('date', value)}
                        error={form.errors.date}
                      />
                    </Box>
                    <Box gap="s8" style={{ width: '50%' }}>
                      <Input
                        label={i18n.t('meetings.create_scheduled_meeting.start_time')}
                        type="time"
                        value={form.values.startTime}
                        onChange={(value) => form.setFieldValue('startTime', value)}
                        error={form.errors.startTime}
                      />
                    </Box>
                  </Box>
                )}
                {!canSeeAdvancedMeetings && <UpsellAlert />}
              </Box>
            </OneModal.Content>
          )}
          <OneModal.Footer>
            <F0Button
              onClick={() => setOpenModal(false)}
              label={i18n.t('common.cancel')}
              variant="outline"
            />
            <F0Button
              onClick={() => form.handleSubmit()}
              label={i18n.t('meetings.create_scheduled_meeting.create_meeting')}
              variant="default"
              isLoading={isLoadingCreateMeeting}
            />
          </OneModal.Footer>
        </>
      )}
    </OneModal>
  )
}
